import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { EntityState, GuestAction, GuestContext, ServerMessage } from '@guest/shared';
import { ApiError, guestApi } from './api.js';

export type ConnectionState = 'connecting' | 'live' | 'offline' | 'ended';

interface HouseStore {
  context: GuestContext | null;
  states: ReadonlyMap<string, EntityState>;
  connection: ConnectionState;
  /** Entity ids with an in-flight action, so the UI can show them settling. */
  pending: ReadonlySet<string>;
  lastError: string | null;
  act: (action: GuestAction) => Promise<void>;
  dismissError: () => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<HouseStore | null>(null);

export function useHouse(): HouseStore {
  const store = useContext(Ctx);
  if (!store) throw new Error('useHouse must be used inside <HouseProvider>');
  return store;
}

/** Reads one entity's live state. */
export function useEntity(entityId: string): EntityState | undefined {
  return useHouse().states.get(entityId);
}

export function HouseProvider({
  children,
  onUnauthenticated,
}: {
  children: ReactNode;
  onUnauthenticated: () => void;
}): ReactNode {
  const [context, setContext] = useState<GuestContext | null>(null);
  const [states, setStates] = useState<ReadonlyMap<string, EntityState>>(new Map());
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [lastError, setLastError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const closedRef = useRef(false);
  const onUnauthRef = useRef(onUnauthenticated);
  onUnauthRef.current = onUnauthenticated;

  useEffect(() => {
    closedRef.current = false;
    let reconnectTimer: number | undefined;
    let pingTimer: number | undefined;

    const connect = (): void => {
      if (closedRef.current) return;
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConnection('live');
        pingTimer = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 25_000);
      };

      ws.onmessage = (ev: MessageEvent<string>) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data) as ServerMessage;
        } catch {
          return;
        }
        switch (msg.type) {
          case 'hello':
            setContext(msg.context);
            break;
          case 'snapshot':
            setStates(new Map(msg.states.map((s) => [s.entityId, s])));
            break;
          case 'state':
            setStates((prev) => {
              const next = new Map(prev);
              next.set(msg.state.entityId, msg.state);
              return next;
            });
            // The house confirmed the change, so stop showing it as settling.
            setPending((prev) => {
              if (!prev.has(msg.state.entityId)) return prev;
              const next = new Set(prev);
              next.delete(msg.state.entityId);
              return next;
            });
            break;
          case 'expired':
            setConnection('ended');
            closedRef.current = true;
            break;
          case 'error':
            setLastError(msg.message);
            break;
          case 'pong':
            break;
        }
      };

      ws.onclose = (ev) => {
        window.clearInterval(pingTimer);
        wsRef.current = null;
        if (closedRef.current) return;
        if (ev.code === 4401) {
          onUnauthRef.current();
          return;
        }
        if (ev.code === 4403) {
          setConnection('ended');
          return;
        }
        setConnection('offline');
        // Exponential backoff, capped — a phone that wakes from sleep should
        // reconnect promptly, but a server that is down shouldn't be hammered.
        const delay = Math.min(1000 * 2 ** retryRef.current++, 15_000);
        reconnectTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => ws.close();
    };

    connect();

    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resync' }));
      } else if (!closedRef.current) {
        retryRef.current = 0;
        window.clearTimeout(reconnectTimer);
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pingTimer);
      wsRef.current?.close();
    };
  }, []);

  const act = useCallback(async (action: GuestAction) => {
    setPending((prev) => new Set(prev).add(action.entityId));
    try {
      await guestApi.act(action);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthRef.current();
        return;
      }
      setLastError(err instanceof Error ? err.message : 'Something went wrong');
      throw err;
    } finally {
      // Clear after a beat even if no state_changed arrives — some entities
      // (scenes, buttons) never report a state we are watching.
      window.setTimeout(() => {
        setPending((prev) => {
          if (!prev.has(action.entityId)) return prev;
          const next = new Set(prev);
          next.delete(action.entityId);
          return next;
        });
      }, 1400);
    }
  }, []);

  const signOut = useCallback(async () => {
    closedRef.current = true;
    wsRef.current?.close();
    await guestApi.signOut().catch(() => undefined);
    onUnauthRef.current();
  }, []);

  const value = useMemo<HouseStore>(
    () => ({
      context,
      states,
      connection,
      pending,
      lastError,
      act,
      dismissError: () => setLastError(null),
      signOut,
    }),
    [context, states, connection, pending, lastError, act, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
